import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { cleanProse } from "../shared/sanitize";
import type { OptionKey } from "../shared/types";

// Everything the crew might be asked about you, and what state each one is in.
//
// Season 0 had no version of this. A member approved questions once at check-in
// and then had no idea what existed, whether there were enough, or whether the
// thing they said in week one was still sitting there waiting to be asked. The
// only way to find out was to wait for a drop.
//
// Three states, because that is genuinely how many there are:
//   waiting on you   generated, not yet approved. Nothing happens until you act.
//   ready            approved and in the pool. Still yours to change or pull.
//   already asked    played. Locked, because the crew has seen it and rewriting
//                    history after the fact would make the game meaningless.

type Row = any;

export function MyQuestions() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>(null);

  async function load() {
    if (!user) return;
    const { data } = await supabase
      .from("question_candidates")
      .select("*")
      .eq("owner_user_id", user.id)
      .eq("league_id", leagueId)
      .order("created_at", { ascending: true });
    setRows(data ?? []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user, leagueId]);

  async function act(id: string, op: string, extra: Record<string, unknown> = {}) {
    setBusy(id); setNote("");
    try {
      await api(`/api/candidates/action`, { body: { op, candidateId: id, ...extra } });
      setEditing(null);
      await load();
      setNote(op === "reject" ? "Pulled. That one won't be asked."
        : op === "edit" ? "Saved. Give it a look and play it when you're happy."
        : "Done.");
    } catch (e: any) {
      setNote(e?.message ?? "That didn't go through. Try again.");
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) {
    return <div className="app"><div className="loading">Loading your questions…</div></div>;
  }

  const waiting = rows.filter((r) => r.status === "pending");
  const ready = rows.filter((r) => r.status === "approved");
  const asked = rows.filter((r) => r.status === "used");
  const pulled = rows.filter((r) => r.status === "rejected");

  return (
    <div className="app">
      <div className="header">
        <h1>Your questions</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}/you`)}>You</button>
      </div>

      <p className="muted">
        Everything the crew might get asked about you. Nothing is playable until you
        say so, and you can pull anything that hasn&apos;t been asked yet.
      </p>

      {note && <div className="banner"><span>{note}</span><button className="link" onClick={() => setNote("")}>dismiss</button></div>}

      {rows.length === 0 && (
        <div className="card live">
          <span className="tag">Nothing yet</span>
          <p>Tell the crew what you&apos;ve been up to and we&apos;ll build questions from it.</p>
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/checkin`)}>
            Check in
          </button>
        </div>
      )}

      {waiting.length > 0 && (
        <>
          <div className="card live">
            <span className="tag">Waiting on you ({waiting.length})</span>
            <p>These aren&apos;t playable yet. Nothing happens until you approve them.</p>
            <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/candidates`)}>
              Go through them
            </button>
          </div>
        </>
      )}

      {ready.length > 0 && (
        <>
          <p className="section-label">Ready to play ({ready.length})</p>
          {ready.map((r) =>
            editing === r.id ? (
              <EditRow
                key={r.id}
                draft={draft}
                onChange={setDraft}
                onCancel={() => setEditing(null)}
                onSave={() => act(r.id, "edit", { question: draft })}
              />
            ) : (
              <div className="card" key={r.id}>
                <p className="q-text">{cleanProse(r.question_text)}</p>
                <p className="muted q-answer">
                  Answer: {cleanProse(r[`option_${r.correct_option as OptionKey}`])}
                </p>
                <div className="row">
                  <button
                    className="btn ghost"
                    disabled={busy === r.id}
                    onClick={() => { setDraft({ ...r }); setEditing(r.id); }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn danger"
                    disabled={busy === r.id}
                    onClick={() => {
                      if (!window.confirm("Pull this one? It won't be asked, and you can always check in again.")) return;
                      void act(r.id, "reject");
                    }}
                  >
                    {busy === r.id ? "Working…" : "Pull it"}
                  </button>
                </div>
              </div>
            )
          )}
        </>
      )}

      {asked.length > 0 && (
        <>
          <p className="section-label">Already asked ({asked.length})</p>
          {asked.map((r) => (
            <div className="card locked" key={r.id}>
              <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                <p className="q-text">{cleanProse(r.question_text)}</p>
                <span className="lock-chip" aria-label="Locked">🔒</span>
              </div>
              <p className="muted q-answer">
                Answer: {cleanProse(r[`option_${r.correct_option as OptionKey}`])}
              </p>
              {/* Locked on purpose, and said out loud rather than just greyed
                  out. The crew has already seen this one; letting somebody
                  rewrite it after the fact would make the whole game meaningless. */}
              <p className="muted" style={{ fontSize: 13 }}>
                The crew has played this one, so it&apos;s locked now.
              </p>
            </div>
          ))}
        </>
      )}

      {pulled.length > 0 && (
        <p className="muted center" style={{ fontSize: 13 }}>
          {pulled.length} {pulled.length === 1 ? "question was" : "questions were"} pulled and
          {pulled.length === 1 ? " isn't" : " aren't"} being used. Nobody sees those.
        </p>
      )}

      <div className="card">
        <span className="tag">Add more any time</span>
        <p className="muted">
          More updates means more questions about you, which is how the crew keeps up.
        </p>
        <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/checkin`)}>
          Add an update
        </button>
      </div>
    </div>
  );
}

function EditRow({ draft, onChange, onCancel, onSave }: {
  draft: any; onChange: (d: any) => void; onCancel: () => void; onSave: () => void;
}) {
  const letters: OptionKey[] = ["a", "b", "c", "d"];
  const set = (k: string, v: string) => onChange({ ...draft, [k]: v });
  return (
    <div className="card">
      <span className="tag">Editing</span>
      <label className="muted">Question</label>
      <textarea className="input" value={draft.question_text} onChange={(e) => set("question_text", e.target.value)} />
      {letters.map((l) => (
        <div key={l}>
          <label className="muted">
            {l.toUpperCase()}{draft.correct_option === l ? " (the right answer)" : ""}
          </label>
          <input className="input" value={draft[`option_${l}`]} onChange={(e) => set(`option_${l}`, e.target.value)} />
        </div>
      ))}
      <label className="muted">What the crew sees afterwards</label>
      <textarea className="input" value={draft.reveal_context} onChange={(e) => set("reveal_context", e.target.value)} />
      {/* An edit un-approves on purpose: what you approved is what gets played,
          so a changed question needs your yes again. */}
      <p className="muted" style={{ fontSize: 13 }}>
        Saving puts this back in Waiting on you, so you can check it before it&apos;s playable.
      </p>
      <div className="row">
        <button className="btn ghost" onClick={onCancel}>Cancel</button>
        <button className="btn primary" onClick={onSave}>Save</button>
      </div>
    </div>
  );
}
